import { BUSINESS_ENTITY, RECENT_TYPE, REPORT_GROUP_TYPE } from '@main/enums';
import { EntityHelpers } from '@main/helpers/entity.helpers';
import { EntityGroup, EntityItem } from '@main/models';
import { FiltersValue } from '@main/reports/shared/services/filters.types';
import { IAccessLogDetails, IAccessLogUnsavedGroup } from '@main/services/recents.service.types';
import { IEntityItemGroupId, IEntityItemInstanceId } from '@main/types';

import { BenchmarkInfo } from '../reports/core/report.types';

export class ViewReportHelpers {
    static composeRequestPayload(
        entities: EntityItem[],
        filters: FiltersValue,
        reportGroup: REPORT_GROUP_TYPE,
        entityType: BUSINESS_ENTITY,
        benchmarkInfo: BenchmarkInfo,
        compare = false,
    ) {
        const isSingleGroup = EntityHelpers.isItemsSingleGroup(entities);

        let instances: IEntityItemInstanceId[] = [];
        let groupsIds: IEntityItemGroupId[] = [];
        let unsavedGroups: IAccessLogUnsavedGroup[] = [];
        let type: RECENT_TYPE;
        let recentEntityType = entityType;

        switch (entityType) {
            case BUSINESS_ENTITY.Advertiser:
            case BUSINESS_ENTITY.Publisher:
            case BUSINESS_ENTITY.Category:
            case BUSINESS_ENTITY.Brand: {
                const filteredEntities = entities.filter((v) => v.entityType === entityType);

                const virtualGroupMembers = this.getVirtualGroups(filteredEntities);

                unsavedGroups = virtualGroupMembers.map((v) => ({
                    name: v.title,
                    instances: v.items.map((item) => item.id) as number[],
                }));

                instances = filteredEntities.filter((v) => v.isInstance()).map((v) => v.id) as IEntityItemInstanceId[];

                groupsIds = filteredEntities
                    .filter((v) => v.isGroup() && !(<EntityGroup>v).isVirtual())
                    .map((v) => v.id) as IEntityItemGroupId[];

                type = this.serializeReportType(filteredEntities, compare, benchmarkInfo);

                if (benchmarkInfo) {
                    recentEntityType = EntityHelpers.mapBenchmarkForEntityType(entityType);
                }

                break;
            }

            case BUSINESS_ENTITY.Campaign:
                instances = entities.map((v) => v.id) as IEntityItemInstanceId[];
                type = RECENT_TYPE.SINGLE;
                break;

            case BUSINESS_ENTITY.Keyword:
                instances = entities.map((v) => v.id) as IEntityItemInstanceId[];
                groupsIds = entities.filter((v) => v.isGroup()).map((v) => v.id) as IEntityItemGroupId[];
                type = instances.length > 1 ? RECENT_TYPE.GROUP : RECENT_TYPE.SINGLE;
                break;
        }

        const payload: IAccessLogDetails = {
            type,
            moduleType: reportGroup,
            entityType: recentEntityType,
            instances,
            favoriteIds: groupsIds,
            unsavedGroups,
            params: EntityHelpers.serializeFiltersParamsForApp(filters),
        };

        if (benchmarkInfo && !isSingleGroup) {
            if (benchmarkInfo.entityType === BUSINESS_ENTITY.Category) {
                payload.categoryId = benchmarkInfo.entityId;
            }
        }

        return payload;
    }

    static serializeReportType(entities: EntityItem[], compare: boolean, benchmarkInfo: BenchmarkInfo): RECENT_TYPE {
        const instancesCount = entities.filter((v) => v.isInstance()).length;
        const hasBenchmark = Boolean(benchmarkInfo);
        const groups = entities.filter((v) => v.isGroup());
        const groupsCount = groups.length;

        if (compare) {
            if (instancesCount > 1 || groupsCount > 0) {
                return RECENT_TYPE.COMPARISON;
            }

            if (instancesCount + groupsCount > 0 && hasBenchmark) {
                return RECENT_TYPE.COMPARISON;
            }

            throw new Error('Error serializing view report token');
        }

        if (instancesCount === 1 && groupsCount === 0) {
            return RECENT_TYPE.SINGLE;
        }

        if (instancesCount > 1) {
            return RECENT_TYPE.GROUP;
        }

        if (groupsCount === 1) {
            return groups[0].items.length === 1 ? RECENT_TYPE.SINGLE : RECENT_TYPE.GROUP;
        }

        throw new Error('Error serializing view report token');
    }

    static getVirtualGroups(entities: EntityItem[]): EntityGroup[] {
        return entities.filter((entity) => entity.isGroup() && (entity as EntityGroup)?.isVirtual()) as EntityGroup[];
    }
}
